import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { X, Send, Loader2, MessageCircle, RotateCcw } from 'lucide-react';
import { useLessonTutor } from './useLessonTutor';
import { LessonTextContent } from './lesson-text/LessonTextContent';
import './lesson-text/LessonDialog.css';

const PHASE_LABELS = { clarify: 'نفهم السؤال', attempt: 'خطوتك التالية', hint: 'تلميح صغير', review: 'نراجع المحاولة', reflection: 'نتأكد من الفهم', practice: 'مثال جديد للتأكد من الفهم', complete: 'أحسنت المحاولة والفهم' };

const LessonChat = ({ open, onClose, lessonId, lessonTitle }) => {
  const [input, setInput] = useState('');
  const { messages, chatId, loadingHistory, sending, error, failedTurn, send, retry } = useLessonTutor(open, lessonId);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const lastAssistant = [...messages].reverse().find(message => message.role === 'assistant');
  const followups = lastAssistant?.phase === 'complete'
    ? ['أريد تحديًا جديدًا', 'أريد مراجعة فكرة أخرى'] : ['لم أفهم هذه الخطوة', 'أحتاج تلميحًا أصغر'];
  useEffect(() => { setInput(''); }, [lessonId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (open && !sending && inputRef.current) {
      const timer = setTimeout(() => inputRef.current?.focus(), 300);
      return () => clearTimeout(timer);
    }
  }, [open, sending]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending || loadingHistory) return;
    setInput('');
    const success = await send(text);
    if (success === false) setInput(current => current || text);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Remove the exiting overlay immediately before another lesson tool opens.
  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="lesson-reader-dialog lesson-chat-dialog max-w-2xl w-[95vw] bg-white border-black/10 text-[#1f1a33] p-0 overflow-hidden max-h-[90vh] flex flex-col"
        data-testid="lesson-chat-dialog"
        data-chat-session-id={chatId || undefined}
      >
        <DialogDescription className="sr-only">المحادثة التعليمية حول محتوى الدرس</DialogDescription>
        {/* Header */}
        <div className="lesson-chat-header bg-gradient-to-l from-teal-600/30 to-cyan-500/20 px-5 py-4 flex items-center justify-between border-b border-black/10 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-teal-500/30 flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-teal-300" />
            </div>
            <div>
              <DialogTitle className="text-base font-bold text-[#1f1a33] tracking-normal" data-testid="chat-title">اسأل عن محتوى الدرس</DialogTitle>
              <p className="text-xs text-[#1f1a33]/50 truncate max-w-[250px]" data-testid="chat-lesson-title">{lessonTitle}</p>
              <span className="lesson-tutor-mode" data-testid="chat-guided-mode">نتعلّم خطوة بخطوة</span>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="text-[#1f1a33]/60 hover:text-[#1f1a33] hover:bg-black/[0.05] rounded-full"
            data-testid="chat-close-btn"
            aria-label="إغلاق المحادثة"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto px-4 py-4 min-h-[300px] max-h-[55vh]" data-testid="chat-messages-area">
          {loadingHistory ? (
            <div className="flex items-center justify-center h-full" data-testid="chat-history-loading">
              <Loader2 className="w-6 h-6 animate-spin text-[#1f1a33]/40" />
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-4">
              <div className="w-16 h-16 rounded-full bg-teal-500/20 flex items-center justify-center mb-4">
                <MessageCircle className="w-8 h-8 text-teal-400" />
              </div>
              <h3 className="text-[#1f1a33]/80 font-bold text-lg mb-2">مرحباً!</h3>
              <p className="text-[#1f1a33]/50 text-sm leading-relaxed max-w-sm">
                ما السؤال الذي تريد التفكير فيه؟ اكتب سؤالك ومحاولتك، أو الجزء الذي حيّرك.
              </p>
              <div className="mt-4 flex flex-wrap gap-2 justify-center">
                {['عندي سؤال ولا أعرف من أين أبدأ', 'أريد مراجعة محاولتي خطوة بخطوة', 'كيف أوجّه طفلي ليحل بنفسه؟'].map((q, i) => (
                  <button
                    key={i}
                    onClick={() => { setInput(q); }}
                    className="text-xs bg-black/[0.03] border border-black/10 rounded-lg px-3 py-2 text-[#1f1a33]/60 hover:bg-black/[0.05] hover:text-[#1f1a33]/80 transition-colors"
                    data-testid={`chat-suggestion-${i}`}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex ${msg.role === 'user' ? 'justify-start' : 'justify-end'}`}
                  data-testid={`chat-message-${idx}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                      msg.role === 'user'
                        ? 'bg-teal-500/20 border border-teal-500/30 rounded-br-sm'
                        : 'lesson-chat-assistant bg-black/[0.03] border border-black/10 rounded-bl-sm'
                    }`}
                  >
                    <p className={`text-xs font-bold mb-1 ${msg.role === 'user' ? 'text-teal-300' : 'text-[#1f1a33]/50'}`}>
                      {msg.role === 'user' ? 'أنت' : <span data-testid={`chat-phase-${idx}`}>{PHASE_LABELS[msg.phase] || 'المرشد التعليمي'}</span>}
                    </p>
                    {msg.role === 'assistant' ? <LessonTextContent compact content={msg.content} testId={`chat-formatted-message-${idx}`} /> : <p
                      className="text-[#1f1a33]/90 text-sm leading-relaxed whitespace-pre-wrap"
                      dir="rtl"
                      style={{ lineHeight: '1.9' }}
                    >
                      {msg.content}
                    </p>}
                    {msg.status === 'failed' && <span className="lesson-tutor-unsent" data-testid={`chat-unsent-${idx}`}>لم يكتمل الإرسال</span>}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex justify-end" data-testid="chat-loading-indicator">
                  <div className="bg-black/[0.03] border border-black/10 rounded-2xl rounded-bl-sm px-4 py-3">
                    <div className="flex items-center gap-2 text-[#1f1a33]/50">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-sm">جارٍ إعداد خطوتك التالية...</span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Input Area */}
        <div className="px-4 py-3 border-t border-black/10 bg-[#f7f5fb] shrink-0">
          {error && <div className="lesson-tutor-error" role="alert" data-testid="chat-error"><p>{error}</p><button type="button" disabled={sending || loadingHistory} onClick={async () => { if (await retry()) setInput(''); }} data-testid="chat-retry"><RotateCcw className="w-3.5 h-3.5" />إعادة المحاولة</button></div>}
          {!error && !loadingHistory && messages.length > 0 && !sending && <div className="lesson-tutor-followups" data-testid="chat-followups">{followups.map((hint, index) => <button key={hint} type="button" onClick={() => { setInput(hint); inputRef.current?.focus(); }} data-testid={`chat-followup-${index}`}>{hint}</button>)}</div>}
          <div className="flex items-center gap-2">
            <div className="flex-1 min-w-0 relative">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="اكتب سؤالك أو محاولتك هنا..."
                disabled={sending || loadingHistory || (Boolean(error) && !failedTurn)}
                aria-label="السؤال أو المحاولة"
                maxLength={4000}
                dir="rtl"
                className="w-full bg-black/[0.03] border border-black/10 rounded-xl px-4 py-3 text-[#1f1a33] placeholder-[#625c78]/60 focus:outline-none focus:border-teal-500/50 focus:ring-1 focus:ring-teal-500/30 text-sm disabled:opacity-50"
                data-testid="chat-input"
              />
            </div>
            <Button
              onClick={handleSend}
              disabled={!input.trim() || sending || loadingHistory || (Boolean(error) && !failedTurn)}
              className="bg-teal-600 hover:bg-teal-500 text-[#1f1a33] rounded-xl h-11 w-11 p-0 shrink-0 disabled:opacity-30"
              data-testid="chat-send-btn"
              aria-label="إرسال المحاولة"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LessonChat;
